import catalog from "../mocks/data/catalog.json";

export const sleep = (ms = 500) => new Promise((r) => setTimeout(r, ms));

const DB_KEY = "mock_db_v1";
function loadDB() {
  try {
    const raw = localStorage.getItem(DB_KEY);
    return raw ? JSON.parse(raw) : { assignments: {}, progress: {} };
  } catch {
    return { assignments: {}, progress: {} };
  }
}
function saveDB(db) {
  localStorage.setItem(DB_KEY, JSON.stringify(db));
}

// Simple login from the earlier step (kept here for completeness)
export async function login({ email, password }) {
  await sleep();
  if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))
    throw new Error("Please enter a valid email");
  if (!password || password.length < 4)
    throw new Error("Incorrect email or password");
  const domain = email.split("@")[1].toLowerCase();
  const isOrgSuperAdmin = email.toLowerCase().startsWith("admin@");
  const isManager = email.toLowerCase().startsWith("manager@");
  return {
    accessToken: `mock_${Math.random().toString(36).slice(2)}`,
    user: {
      id: `u_${Math.random().toString(36).slice(2, 8)}`,
      name: isOrgSuperAdmin ? "Org Super Admin" : "Employee User",
      email,
      tenantId: `t_${domain.replace(/\W/g, "_")}`,
      roles: [isOrgSuperAdmin ? "org_super_admin" : "employee"],
      derived: { isManager: isManager },
    },
  };
}

export function seedAssignmentsForUser(userId) {
  const db = loadDB();
  if (!db.assignments[userId]) {
    db.assignments[userId] = [
      { trainingId: "safety-basics", status: "not-started" },
    ];
    saveDB(db);
  }
}

export async function listMyAssignments(userId) {
  await sleep(250);
  seedAssignmentsForUser(userId);
  const db = loadDB();
  const items = db.assignments[userId] || [];
  // Enrich with catalog metadata
  return items
    .filter((a) => a.status !== "passed") // only due trainings
    .map((a) => {
      const t = catalog.find((x) => x.id === a.trainingId);
      return {
        trainingId: a.trainingId,
        status: a.status,
        title: t?.title || a.trainingId,
        description: t?.description || "",
        stepsCount: t?.steps?.length || 0,
      };
    });
}

export async function getTrainingById(id) {
  await sleep(150);
  const t = catalog.find((x) => x.id === id);
  if (!t) throw new Error("Training not found");
  return t;
}

export async function completeTraining(userId, trainingId) {
  const db = loadDB();
  const list = db.assignments[userId] || [];
  const idx = list.findIndex((a) => a.trainingId === trainingId);
  if (idx >= 0) {
    list[idx].status = "passed";
    db.assignments[userId] = list;
    saveDB(db);
  }
  await sleep(100);
  return { ok: true };
}

export async function submitQuiz(userId, trainingId, answers) {
  // Find quiz step in catalog
  const t = catalog.find((x) => x.id === trainingId);
  const quiz = t?.steps?.find((s) => s.type === "quiz");
  if (!quiz) throw new Error("Quiz not found");
  const total = quiz.questions.length;
  let correct = 0;
  quiz.questions.forEach((q, i) => {
    if (answers[i] === q.answerIndex) correct++;
  });
  const passed = correct === total; // require all correct for MVP
  await sleep(400);
  if (passed) await completeTraining(userId, trainingId);
  return { correct, total, passed };
}

// below other exports
export async function getAssignmentStatus(userId, trainingId) {
  await sleep(100);
  seedAssignmentsForUser(userId);
  const db = (() => {
    try {
      return JSON.parse(localStorage.getItem("mock_db_v1")) || {};
    } catch {
      return {};
    }
  })();
  const list = db.assignments?.[userId] || [];
  const item = list.find((a) => a.trainingId === trainingId);
  return item?.status || "not-assigned"; // "not-started" | "passed" | etc.
}

export function seedTeamForManager(manager) {
  const db = loadDB();
  db.users = db.users || {};
  db.hierarchy = db.hierarchy || {};
  if (db.hierarchy[manager.id]) return; // already seeded
  const domain = (manager.email.split("@")[1] || "example.com").toLowerCase();
  const r1 = {
    id: `r1_${manager.id}`,
    name: "Sam Report",
    email: `sam@${domain}`,
  };
  const r2 = {
    id: `r2_${manager.id}`,
    name: "Jo Report",
    email: `jo@${domain}`,
  };
  db.users[r1.id] = r1;
  db.users[r2.id] = r2;
  db.hierarchy[manager.id] = [r1.id, r2.id];
  saveDB(db);
  seedAssignmentsForUser(r1.id);
  seedAssignmentsForUser(r2.id);
}

export async function listTeamProgress(managerId) {
  await sleep(200);
  const db = loadDB();
  const ids = db.hierarchy?.[managerId] || [];
  return ids.map((uid) => {
    const u = db.users?.[uid] || { id: uid, name: "User", email: "" };
    const assigns = db.assignments?.[uid] || [];
    const trainings = assigns.map((a) => {
      const t = catalog.find((x) => x.id === a.trainingId);
      return {
        trainingId: a.trainingId,
        title: t?.title || a.trainingId,
        status: a.status,
      };
    });
    const dueCount = trainings.filter((t) => t.status !== "passed").length;
    const completedCount = trainings.filter(
      (t) => t.status === "passed"
    ).length;
    return {
      userId: uid,
      name: u.name,
      email: u.email,
      trainings,
      dueCount,
      completedCount,
    };
  });
}

export async function setAssignmentStatus(userId, trainingId, status) {
  const db = loadDB();
  const list = db.assignments[userId] || [];
  const idx = list.findIndex((a) => a.trainingId === trainingId);
  if (idx >= 0) list[idx].status = status;
  else list.push({ trainingId, status });
  db.assignments[userId] = list;
  saveDB(db);
  await sleep(150);
  return { ok: true };
}
